import { LANE_ID_ATTRIBUTE, PLACEMENT_KEY_ATTRIBUTE } from "./boardAttributes";

interface BoardScrollAnchor {
    placementKey: string;
    /** From the scroller's top edge to the card's, in layout (no transforms). */
    offset: number;
}

export type BoardListKind = "plain" | "windowed";

/**
 * A scroller's layout just before some of its lists unmounted, for lists of
 * the other kind replacing them in the same commit: crossing the windowing
 * threshold swaps plain lists for windowed ones (or back), which would
 * otherwise jump.
 */
interface BoardScrollSnapshot {
    /** What the lists that unmounted were. */
    kind: BoardListKind;
    /** Their cells; the lane list has none. */
    cellKeys: Set<string>;
    scrollTop: number;
    /** Heights of the mounted cards (by placement key) and lanes (by id). */
    sizes: ReadonlyMap<string, number>;
    /** The first cards in view, so one surviving the change is enough. */
    anchors: readonly BoardScrollAnchor[];
}

// Cards in view kept as anchors: the first ones can be the card that was
// deleted or moved away.
const ANCHOR_COUNT = 3;

const snapshots = new WeakMap<HTMLElement, BoardScrollSnapshot>();

const readKey = (element: Element) =>
    element.getAttribute(PLACEMENT_KEY_ATTRIBUTE) ??
    element.getAttribute(LANE_ID_ATTRIBUTE);

/**
 * Where `element` sits in the scroller's content, from offsets: neither the
 * scroll nor drag transforms move it. Stops at the scroller when it's on
 * the offset chain (a column); otherwise both chains reach the page.
 */
const getContentTop = (element: HTMLElement, scroller: HTMLElement) => {
    let top = 0;
    for (
        let node: Element | null = element;
        node instanceof HTMLElement;
        node = node.offsetParent
    ) {
        top += node.offsetTop;
        if (node.offsetParent === scroller) {
            return top;
        }
    }
    for (
        let node: Element | null = scroller;
        node instanceof HTMLElement;
        node = node.offsetParent
    ) {
        top -= node.offsetTop;
    }
    return top - scroller.clientTop;
};

export interface BoardListArgs {
    scroller: HTMLElement;
    kind: BoardListKind;
    /** The list's cell; null for the lane list, which stands for them all. */
    cellKey: string | null;
}

/**
 * Call as a list unmounts, while its cards are still in the DOM, unless it
 * leaves with its lane. Lists sharing the scroller unmount together: the
 * first one takes the snapshot and the rest add their cells. Nothing above
 * the viewport can shift at the top, so a scroller there takes none.
 */
export const captureScrollSnapshot = ({
    scroller,
    kind,
    cellKey,
}: BoardListArgs) => {
    const existing = snapshots.get(scroller);
    if (existing !== undefined) {
        if (existing.kind === kind && cellKey !== null) {
            existing.cellKeys.add(cellKey);
        }
        return;
    }
    const { scrollTop } = scroller;
    if (scrollTop === 0) {
        return;
    }
    const sizes = new Map<string, number>();
    const anchors: BoardScrollAnchor[] = [];
    scroller
        .querySelectorAll<HTMLElement>(
            `[${PLACEMENT_KEY_ATTRIBUTE}], [${LANE_ID_ATTRIBUTE}]`,
        )
        .forEach((element) => {
            const key = readKey(element);
            if (key === null) {
                return;
            }
            sizes.set(key, element.offsetHeight);
            const placementKey = element.getAttribute(PLACEMENT_KEY_ATTRIBUTE);
            const offset = getContentTop(element, scroller) - scrollTop;
            if (
                placementKey !== null &&
                anchors.length < ANCHOR_COUNT &&
                offset + element.offsetHeight > 0
            ) {
                anchors.push({ placementKey, offset });
            }
        });
    const cellKeys = new Set(cellKey === null ? [] : [cellKey]);
    snapshots.set(scroller, { kind, cellKeys, scrollTop, sizes, anchors });
    // Lists mounting in this commit read it; it's stale for any later one.
    queueMicrotask(() => snapshots.delete(scroller));
};

/**
 * The snapshot a list mounting as `kind` takes over: one left this commit
 * by lists of the other kind, its own cell among them. The lane list takes
 * any, since it replaces every cell.
 */
export const readScrollSnapshot = ({
    scroller,
    kind,
    cellKey,
}: BoardListArgs) => {
    const snapshot = snapshots.get(scroller);
    return snapshot !== undefined &&
        snapshot.kind !== kind &&
        (cellKey === null || snapshot.cellKeys.has(cellKey))
        ? snapshot
        : undefined;
};

/**
 * For plain lists mounting in place of windowed ones: scrolls so the first
 * anchor card still in the scroller is back where it was. The windowed
 * lists sized what they never mounted by estimate; these are real. Only the
 * first list to mount in the commit restores.
 */
export const restoreScrollAnchor = (scroller: HTMLElement, cellKey: string) => {
    const snapshot = readScrollSnapshot({ scroller, kind: "plain", cellKey });
    if (snapshot === undefined) {
        return;
    }
    snapshots.set(scroller, { ...snapshot, anchors: [] });
    const slots = new Map(
        Array.from(
            scroller.querySelectorAll<HTMLElement>(
                `[${PLACEMENT_KEY_ATTRIBUTE}]`,
            ),
            (slot) => [slot.getAttribute(PLACEMENT_KEY_ATTRIBUTE), slot],
        ),
    );
    const anchor = snapshot.anchors.find(({ placementKey }) =>
        slots.has(placementKey),
    );
    const slot = anchor && slots.get(anchor.placementKey);
    if (anchor !== undefined && slot !== undefined) {
        scroller.scrollTop = getContentTop(slot, scroller) - anchor.offset;
    }
};
